import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Inject,
  AfterViewInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormStateService } from '../../../services/formly/form-state.service';
import {
  checkForValidFields,
  populateFormFields,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
  configureFields,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { Subscription, filter } from 'rxjs';
import { FormlyValueChangeEvent } from '@ngx-formly/core/lib/models';
import { IEnvironment } from '../../../models/environment.model';
import {
  EditableInputType,
  EditableInputValueChange,
} from '../../../components/irembo-editable-input/irembo-editable-input.component';

@Component({
  standalone: false,
  selector: 'irembogov-custom-editable-input',
  templateUrl: './custom-editable-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomEditableInputComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy, AfterViewInit
{
  private subscriptions = new Subscription();
  fieldsToPopulate: Record<string, string>[] = [];
  inputType: EditableInputType = 'text';
  items: Record<string, unknown>[] = [];
  loading = false;
  private readonly environment: IEnvironment;

  constructor(
    private formStateService: FormStateService,
    private readonly cd: ChangeDetectorRef,
    private readonly http: HttpClient,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
  }

  ngOnInit(): void {
    checkForValidFields(this.field);

    this.inputType =
      (this.field?.props?.['inputType'] as EditableInputType) ?? 'text';
    this.fieldsToPopulate = this.field?.props?.['populates'] ?? [];

    if (Array.isArray(this.field?.props?.['options'])) {
      this.items = this.field.props['options'] as Record<string, unknown>[];
    }

    this.getDataSet();
    this.setupFieldPopulation();
    this.processDefaultValue();
  }

  ngAfterViewInit(): void {
    if (!this.field?.options?.fieldChanges) return;

    this.subscriptions.add(
      this.field.options.fieldChanges
        .pipe(
          filter((e: FormlyValueChangeEvent) => {
            return (
              e.field.key === this.field.key &&
              e.type === 'expressionChanges' &&
              (e['property']?.startsWith('props.dataset.params') ||
                e['property'] === 'props.options')
            );
          })
        )
        .subscribe((e: FormlyValueChangeEvent) => {
          if (e['property'] === 'props.options') {
            this.field.props = this.field.props ?? {};
            this.field.props.options = e.field.props?.options ?? [];
            this.items = (e.field.props?.options ?? []) as Record<
              string,
              unknown
            >[];
            this.cd.detectChanges();
          } else {
            this.getDataSet();
          }
        })
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  onValueChange(event: EditableInputValueChange): void {
    if (!this.fieldsToPopulate?.length) return;

    this.formStateService.saveFetchedDataKeyInFormState(this.field, event);
    if (this.field.formControl.valid) {
      const populateForm = getPopulateReferenceForm(this.field, this.form);
      if (!populateForm) return;

      populateFormFields(
        event,
        this.field,
        populateForm,
        this.formStateService
      );
    } else {
      this.field.options?.detectChanges?.(this.field);
    }
  }

  getDataSet(): void {
    if (this.loading) return;

    if (
      Array.isArray(this.field?.props?.['options']) &&
      this.field.props['options'].length > 0
    ) {
      this.items = this.field.props['options'] as Record<string, unknown>[];
      return;
    }

    const dataset = this.field?.props?.['dataset'];
    if (!dataset?.url) return;

    const httpMethod = dataset['httpMethod'] ?? 'GET';
    let url = dataset['url'];
    const params = { ...(dataset['params'] ?? {}) };
    const headers = dataset['headers'] ?? {};
    const dataField = dataset['dataField'] ?? '';

    if (this.field.props?.['useBaseUrl']) {
      try {
        url = this.updateUrlWithApiGatewayBaseUrl(url);
      } catch (error) {
        console.error('Error updating URL with API gateway base URL:', error);
        return;
      }
    }

    if (httpMethod === 'POST') {
      this.getDataByPostMethod(url, dataField, params, headers);
    } else {
      this.getDataByGetMethod(url, dataField, params, headers);
    }
  }

  private setupFieldPopulation(): void {
    if (this.fieldsToPopulate.length > 0) {
      const populateForm = getPopulateReferenceForm(this.field, this.form);
      if (populateForm) {
        this.subscriptions.add(
          configureFields(this.field, this.fieldsToPopulate, populateForm)
        );
      }
    }

    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        getPopulateReferenceForm(this.field, this.form)
      )
    );
  }

  private processDefaultValue(): void {
    const defaultValue = this.field?.props?.['initialValue'];

    if (
      defaultValue &&
      typeof defaultValue === 'object' &&
      'newValue' in defaultValue
    ) {
      return;
    }

    if (
      defaultValue &&
      (typeof defaultValue === 'string' || typeof defaultValue === 'number')
    ) {
      const stringValue = String(defaultValue);
      const valueObject: EditableInputValueChange = {
        status: false,
        previousValue: stringValue,
        newValue: stringValue,
      };

      if (this.inputType === 'dropdown' && this.items.length > 0) {
        const bindValueKey = this.field?.props?.['bindValue'] || 'value';
        const bindLabelKey = this.field?.props?.['bindLabel'] || 'label';

        const item = this.items.find(
          item => item?.[bindValueKey] === stringValue
        );

        if (item) {
          valueObject.datasetValueName = item[bindLabelKey] as string;
          valueObject.datasetValueCode = item[bindValueKey] as string;
        }
      }

      if (this.field.props) {
        this.field.props['initialValue'] = valueObject;
      }
    }
  }

  private updateUrlWithApiGatewayBaseUrl(url: string): string {
    const portalApiGatewayBaseUrl = this.environment?.apiGatewayBaseUrl;
    if (!portalApiGatewayBaseUrl) {
      throw new Error(
        'useBaseUrl: portal environment ApiGatewayBaseUrl property is required'
      );
    }
    return `${portalApiGatewayBaseUrl}${url}`;
  }

  private getDataByPostMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {}
  ): void {
    this.loading = true;
    this.subscriptions.add(
      this.http
        .post<Record<string, unknown> | []>(url, params, { headers })
        .subscribe({
          next: res => {
            this.handleDataResponse(res, dataField);
          },
          error: err => {
            console.error(
              'Error fetching data:',
              err instanceof Error ? err.message : 'Unknown error'
            );
            this.loading = false;
            this.cd.detectChanges();
          },
        })
    );
  }

  private getDataByGetMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {}
  ): void {
    const queryParams = Object.entries(params)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');

    const queryString = queryParams ? `?${queryParams}` : '';

    this.loading = true;
    this.subscriptions.add(
      this.http
        .get<Record<string, unknown> | []>(`${url}${queryString}`, { headers })
        .subscribe({
          next: res => {
            this.handleDataResponse(res, dataField);
          },
          error: err => {
            console.error(
              'Error fetching data:',
              err instanceof Error ? err.message : 'Unknown error'
            );
            this.loading = false;
            this.cd.detectChanges();
          },
        })
    );
  }

  private handleDataResponse(
    res: Record<string, unknown> | [],
    dataField: string
  ): void {
    this.items = this.extractDataFromResponse(res, dataField);

    // Check if current form control value has dataset info that's not in items
    const currentValue = this.formControl.value;
    if (
      currentValue &&
      typeof currentValue === 'object' &&
      'datasetValueCode' in currentValue &&
      'datasetValueName' in currentValue &&
      this.inputType === 'dropdown'
    ) {
      const bindValueKey = this.field?.props?.['bindValue'] || 'value';
      const bindLabelKey = this.field?.props?.['bindLabel'] || 'label';

      // Check if the datasetValueCode exists in the fetched items
      const existingItem = this.items.find(
        item => item?.[bindValueKey] === currentValue.datasetValueCode
      );

      // If it doesn't exist, add it to the items array
      if (
        !existingItem &&
        currentValue.datasetValueCode &&
        currentValue.datasetValueName
      ) {
        const newItem = {
          [bindValueKey]: currentValue.datasetValueCode,
          [bindLabelKey]: currentValue.datasetValueName,
        };

        // Add the item to the beginning of the array so it's easily visible
        this.items.unshift(newItem);
      }
    }

    if (this.field.props) {
      this.field.props['options'] = this.items;
    }

    this.processDefaultValue();
    this.loading = false;
    this.cd.detectChanges();
  }

  private extractDataFromResponse(
    res: Record<string, unknown> | [],
    dataField: string
  ): Record<string, unknown>[] {
    if (!res) return [];

    if (Array.isArray(res)) {
      return res;
    }

    if (!dataField) {
      return [];
    }

    try {
      const keys = dataField.split('.');
      let value: any = res;

      for (const key of keys) {
        if (!value || value[key] === undefined) {
          return [];
        }
        value = value[key];
      }

      return Array.isArray(value) ? value : [];
    } catch (error) {
      console.error(
        'Error extracting data from response:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      return [];
    }
  }
}
